import type { ReactNode } from "react";
import Image from "next/image";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import {
  Activity,
  ChevronRight,
  GaugeCircleIcon,
  HardDrive,
  History,
  Layers,
  Ruler,
  Terminal,
} from "lucide-react";
import MigrationWizardSvg from "@/assets/migration-wizard.svg";
import { AppSidebarHeaderSkeleton } from "../ui/app-header";
import { RAIL_ROW_ACTIVE } from "./SidebarGlobalChrome";
import { isAdminPath, isSettingsPath } from "./useActiveApp";
import { cn } from "@repo/blacksmith-ui/cn";
import { readCachedSidebarVariant } from "../codesmith/sidebarVariant";

interface SidebarSkeletonProps {
  collapsed?: boolean;
  /** Standalone account pages (usage/settings/support/admin): the Actions
   * sidebar hides its nav there, so the skeleton omits the migration and
   * nav rows. */
  standalone?: boolean;
}

/**
 * Skeleton for the GitHub Actions contextual sidebar. Matches Sidebar.tsx
 * layout and DESIGN.md (including avoiding layout shift).
 *
 * The sidebar's chrome and nav are fixed: none of it depends on the data being
 * awaited, so every fixed glyph and label renders as the mounted sidebar's own
 * content rather than a gray block that would only flash before the identical
 * row lands (the same treatment as RailSkeleton in SidebarLayoutSkeleton.tsx).
 */
type SkeletonNavItem = {
  name: string;
  icon: ReactNode;
  trailing?: "chevron";
};

// Mirrors Sidebar's MainNav: hover-card rows (Run History, Analytics,
// Storage) show a trailing chevron.
const mainNav: SkeletonNavItem[] = [
  {
    name: "Run History",
    icon: <History className="size-[18px]" />,
    trailing: "chevron",
  },
  { name: "Monitors", icon: <Activity className="size-[18px]" /> },
  { name: "Rightsize Runners", icon: <Ruler className="size-[18px]" /> },
  { name: "Machine Definitions", icon: <Layers className="size-[18px]" /> },
  { name: "Logs", icon: <Terminal className="size-[18px]" /> },
  {
    name: "Analytics",
    icon: <GaugeCircleIcon className="size-[18px]" />,
    trailing: "chevron",
  },
  {
    name: "Storage",
    icon: <HardDrive className="size-[18px]" />,
    trailing: "chevron",
  },
];

/** Mirrors Sidebar's route-derived active nav item (via isActive) so the
 * active row's fill doesn't pop in on swap. Route templates are enough here:
 * the org segment carries no signal for which section is active. */
function activeNavName(pathname: string): string | null {
  const path = pathname.replace(/^\/\[org\]/, "") || "/";
  if (path === "/" || path.startsWith("/runs")) return "Run History";
  if (path.startsWith("/monitors")) return "Monitors";
  if (path.startsWith("/rightsize")) return "Rightsize Runners";
  if (path.startsWith("/machine-definitions")) return "Machine Definitions";
  if (path.startsWith("/logs")) return "Logs";
  if (path.startsWith("/analytics") || path.startsWith("/docker-analytics")) {
    return "Analytics";
  }
  if (path.startsWith("/stickydisks") || path.startsWith("/cache")) {
    return "Storage";
  }
  return null;
}

/** Non-interactive twin of the mounted sidebar's expanded nav row: same 32px
 * footprint, icon, label, and trailing chevron, at resting colors,
 * without the hover/focus affordances a placeholder shouldn't advertise. */
function NavRowSkeleton({
  item,
  active,
}: {
  item: SkeletonNavItem;
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        // border-transparent mirrors SidebarMenuButton's 1px focus border, so
        // the icon and label sit at the same 7px content inset as the mounted
        // row instead of shifting 1px on swap.
        "flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular",
        "text-neutral-800 [&_svg]:text-neutral-600 dark:text-neutral-300 dark:[&_svg]:text-neutral-400",
        active && RAIL_ROW_ACTIVE,
      )}
    >
      {item.icon}
      <span className="whitespace-nowrap">{item.name}</span>
      {item.trailing === "chevron" && (
        // size-4, not the size-[18px] the mounted row declares: there the
        // chevron is a direct child of SidebarMenuButton, whose higher-
        // specificity [&>svg]:size-4 clamps it to 16px. This bakes in that
        // computed size (the leading icons escape the clamp via their
        // wrapper div and really are 18px).
        <ChevronRight className="ml-auto size-4 text-neutral-500 dark:text-neutral-400" />
      )}
    </div>
  );
}

/** Collapsed twin: the 32px icon-only button at resting colors. */
function CollapsedNavRowSkeleton({
  item,
  active,
}: {
  item: SkeletonNavItem;
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
        "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400",
        active && RAIL_ROW_ACTIVE,
      )}
    >
      {item.icon}
    </div>
  );
}

/** MigrationWizard's own icon + label at its row geometry (rounded-sm and its
 * bespoke resting colors, matching the trigger button in LazyMigrationWizard.tsx). */
function MigrationRowSkeleton({ collapsed }: { collapsed?: boolean }) {
  const icon = (
    <span className="flex size-[18px] shrink-0 items-center justify-center">
      <Image
        src={MigrationWizardSvg}
        alt=""
        aria-hidden="true"
        width={16}
        height={16}
        className="size-4"
      />
    </span>
  );
  if (collapsed) {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm">
        {icon}
      </div>
    );
  }
  return (
    // border-transparent mirrors the trigger button's 1px focus border (see
    // NavRowSkeleton) so the icon and label land at the mounted row's inset.
    <div className="flex h-8 w-full items-center gap-2 rounded-sm border border-transparent p-1.5 type-body-s-regular text-[#434546] dark:text-[#bbb]">
      {icon}
      <span className="whitespace-nowrap">Migration Wizard</span>
    </div>
  );
}

/** The migration row + nav rows shared by every non-standalone variant. */
function NavBlockSkeleton({
  collapsed,
  activeName,
}: {
  collapsed?: boolean;
  activeName: string | null;
}) {
  return (
    <>
      <MigrationRowSkeleton collapsed={collapsed} />
      {mainNav.map((item) =>
        collapsed ? (
          <CollapsedNavRowSkeleton
            key={item.name}
            item={item}
            active={activeName === item.name}
          />
        ) : (
          <NavRowSkeleton
            key={item.name}
            item={item}
            active={activeName === item.name}
          />
        ),
      )}
    </>
  );
}

export default function SidebarSkeleton({
  collapsed,
  standalone,
}: SidebarSkeletonProps) {
  const router = useRouter();
  const activeName = activeNavName(router.pathname);
  const urlOrg = router.query.org;
  const redesign =
    readCachedSidebarVariant(typeof urlOrg === "string" ? urlOrg : null) ===
    "redesign";
  const headerGapClass = redesign
    ? "pt-0"
    : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  const rowGapClass = redesign ? "gap-0" : "gap-1";
  // The standalone variant also stands in for SettingsSidebar and
  // AdminSidebar, and on the other standalone account paths no contextual
  // sidebar mounts at all, so only settings and admin routes have a knowable
  // title; the rest keep a neutral placeholder bar.
  const title = standalone
    ? isSettingsPath(router.pathname)
      ? "Settings"
      : isAdminPath(router.pathname)
        ? "Admin"
        : null
    : "GitHub Actions";

  if (collapsed) {
    return (
      <div
        className="flex h-full w-[var(--sidebar-width-icon)] flex-col pointer-events-none border-r border-[#0c0c0d14] dark:border-white/12 bg-sidebar"
        data-slot="sidebar-skeleton"
      >
        <AppSidebarHeaderSkeleton collapsed />

        {/* Content - the mounted SidebarGroup's collapsed padding: pl-3/pr-1
            (the offset next to the rail) with the GitHub Actions header gap.
            The inner column's -mx-1 mirrors the nav rows' own -mx-1, which
            puts the icon centerline at 28px, not the sidebar's midline. */}
        <div
          className={cn(
            "flex flex-1 flex-col justify-start min-h-0 overflow-hidden pb-0 pl-3 pr-1 w-full",
            headerGapClass,
          )}
          style={{ minHeight: "200px" }}
        >
          {!standalone && (
            <div
              className={cn(
                "-mx-1 flex flex-1 flex-col items-center min-h-0",
                rowGapClass,
              )}
            >
              <NavBlockSkeleton collapsed activeName={activeName} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full w-[var(--sidebar-width)] flex-col pointer-events-none border-r border-[#0c0c0d14] dark:border-white/12 bg-sidebar"
      data-slot="sidebar-skeleton"
    >
      <AppSidebarHeaderSkeleton title={title ?? undefined} />

      {/* Content - migration row + nav rows. px-2 and headerGapClass match the
          mounted SidebarGroup's net padding; its inner SidebarGroupContent px-1
          is cancelled by the rows' -mx-1, so rows span the full px-2 gutter. */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-0",
          headerGapClass,
        )}
        style={{ minHeight: "200px" }}
      >
        {!standalone && (
          <div className={cn("flex flex-col", rowGapClass)}>
            <NavBlockSkeleton activeName={activeName} />
          </div>
        )}
      </div>
    </div>
  );
}
